import { useState, useEffect, useRef, useCallback } from 'react'
import {
  Loader2,
  AlertCircle,
  ArrowLeft,
  LogOut,
  Search,
  Upload,
  FileText,
  ChevronDown,
  ChevronUp,
  Users,
  Save,
  Replace,
} from 'lucide-react'
import { Link } from 'react-router-dom'
import clsx from 'clsx'
import toast from 'react-hot-toast'
import Swal from 'sweetalert2'
import { useAuth } from '@/hooks/useAuth'
import { LEAD_MAGNETS } from '@/data/landing'
import type { Resource, Downloader } from '@/types'

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

export default function AdminResources() {
  const { user, loading: authLoading, signInWithGoogle, signOut, getIdToken } = useAuth()
  const [resources, setResources] = useState<Resource[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [signInError, setSignInError] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState('')

  // Comments editing
  const [editingSlug, setEditingSlug] = useState<string | null>(null)
  const [editingComments, setEditingComments] = useState('')
  const [savingComments, setSavingComments] = useState(false)

  // Downloaders expansion
  const [expandedSlug, setExpandedSlug] = useState<string | null>(null)
  const [downloaders, setDownloaders] = useState<Record<string, Downloader[]>>({})
  const [loadingDownloaders, setLoadingDownloaders] = useState(false)

  // Upload
  const [uploading, setUploading] = useState(false)
  const [uploadSlug, setUploadSlug] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const newFileInputRef = useRef<HTMLInputElement>(null)
  const [newSlug, setNewSlug] = useState('')

  const handleSignIn = async () => {
    try {
      setSignInError(null)
      await signInWithGoogle()
    } catch (err) {
      setSignInError(err instanceof Error ? err.message : 'Sign-in failed')
    }
  }

  const fetchResources = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const idToken = await getIdToken()
      const response = await fetch('/api/resources/admin', {
        headers: { Authorization: `Bearer ${idToken}` },
      })
      if (response.status === 401 || response.status === 403) {
        const data = await response.json()
        throw new Error(data.error || 'Access denied')
      }
      if (!response.ok) throw new Error('Failed to fetch resources')
      const data = await response.json()
      setResources(data.resources)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred')
    } finally {
      setLoading(false)
    }
  }, [getIdToken])

  useEffect(() => {
    if (!user) return
    fetchResources()
  }, [user, fetchResources])

  // Save comments
  const handleSaveComments = async (slug: string) => {
    setSavingComments(true)
    try {
      const idToken = await getIdToken()
      const response = await fetch(`/api/resources/admin/${slug}/comments`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${idToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ comments: editingComments }),
      })
      if (!response.ok) throw new Error('Failed to save comments')
      setResources((prev) =>
        prev.map((r) => (r.slug === slug ? { ...r, comments: editingComments } : r)),
      )
      setEditingSlug(null)
      toast.success('Comments saved')
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to save')
    } finally {
      setSavingComments(false)
    }
  }

  // Toggle downloaders
  const handleToggleDownloaders = async (slug: string) => {
    if (expandedSlug === slug) {
      setExpandedSlug(null)
      return
    }
    setExpandedSlug(slug)
    if (downloaders[slug]) return

    setLoadingDownloaders(true)
    try {
      const idToken = await getIdToken()
      const response = await fetch(`/api/resources/admin/${slug}/downloaders`, {
        headers: { Authorization: `Bearer ${idToken}` },
      })
      if (!response.ok) throw new Error('Failed to load downloaders')
      const data = await response.json()
      setDownloaders((prev) => ({ ...prev, [slug]: data.downloaders }))
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to load downloaders')
      setExpandedSlug(null)
    } finally {
      setLoadingDownloaders(false)
    }
  }

  // Upload / replace file
  const handleUploadFile = async (file: File, slug: string) => {
    setUploading(true)
    setUploadSlug(slug)
    try {
      const idToken = await getIdToken()
      const formData = new FormData()
      formData.append('file', file)
      formData.append('slug', slug)

      const response = await fetch('/api/resources/admin/upload', {
        method: 'POST',
        headers: { Authorization: `Bearer ${idToken}` },
        body: formData,
      })
      if (!response.ok) {
        const data = await response.json()
        throw new Error(data.error || 'Upload failed')
      }
      toast.success(`Uploaded ${slug}.pdf`)
      await fetchResources()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Upload failed')
    } finally {
      setUploading(false)
      setUploadSlug(null)
    }
  }

  const handleReplaceClick = (slug: string) => {
    Swal.fire({
      title: 'Replace file?',
      text: `This will overwrite ${slug}.pdf in storage.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#4A2D7A',
      cancelButtonColor: '#6B5F7B',
      confirmButtonText: 'Choose file',
    }).then((result) => {
      if (result.isConfirmed && fileInputRef.current) {
        fileInputRef.current.dataset.slug = slug
        fileInputRef.current.click()
      }
    })
  }

  const handleFileSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    const slug = e.target.dataset.slug
    if (file && slug) {
      handleUploadFile(file, slug)
    }
    e.target.value = ''
  }

  // Upload new file
  const handleNewFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    // Build dropdown options from known lead magnets, excluding ones already uploaded
    const uploadedSlugs = new Set(resources.map((r) => r.slug))
    const options = LEAD_MAGNETS.map(
      (lm) =>
        `<option value="${lm.slug}" ${uploadedSlugs.has(lm.slug) ? 'style="color:#9589A3;"' : ''}>${lm.title}${uploadedSlugs.has(lm.slug) ? ' (replace)' : ''}</option>`,
    ).join('')

    Swal.fire({
      title: 'Upload resource',
      html: `<p style="color:#6B5F7B;font-size:14px;margin-bottom:12px;">File: <strong>${file.name}</strong></p>
             <select id="swal-slug" class="swal2-select" style="font-size:14px;padding:8px 12px;border:1px solid #E5DDD0;border-radius:8px;width:100%;color:#1C1228;">
               <option value="" disabled selected>Choose a resource...</option>
               ${options}
               <option value="__custom__">Custom slug...</option>
             </select>
             <input id="swal-custom-slug" class="swal2-input" placeholder="custom-slug-name" style="font-size:14px;display:none;margin-top:8px;">`,
      showCancelButton: true,
      confirmButtonColor: '#4A2D7A',
      confirmButtonText: 'Upload',
      didOpen: () => {
        const select = document.getElementById('swal-slug') as HTMLSelectElement
        const customInput = document.getElementById('swal-custom-slug') as HTMLInputElement
        select?.addEventListener('change', () => {
          customInput.style.display = select.value === '__custom__' ? 'block' : 'none'
        })
      },
      preConfirm: () => {
        const select = document.getElementById('swal-slug') as HTMLSelectElement
        const customInput = document.getElementById('swal-custom-slug') as HTMLInputElement
        if (!select?.value) {
          Swal.showValidationMessage('Please select a resource')
          return false
        }
        if (select.value === '__custom__') {
          const slug = customInput?.value?.trim()
          if (!slug) {
            Swal.showValidationMessage('Enter a slug')
            return false
          }
          return slug
        }
        return select.value
      },
    }).then((result) => {
      if (result.isConfirmed && result.value) {
        handleUploadFile(file, result.value)
      }
    })
    e.target.value = ''
  }

  // Filter resources by search
  const filtered = resources.filter((r) => {
    if (!searchQuery) return true
    const q = searchQuery.toLowerCase()
    return (
      r.title.toLowerCase().includes(q) ||
      r.slug.toLowerCase().includes(q) ||
      r.fileName.toLowerCase().includes(q) ||
      r.comments.toLowerCase().includes(q)
    )
  })

  // Loading spinner while Firebase restores persisted session
  if (authLoading) {
    return (
      <div className="min-h-screen bg-bg-main flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-brand-mid" />
      </div>
    )
  }

  // Sign-in screen
  if (!user) {
    return (
      <div className="min-h-screen bg-bg-main flex items-center justify-center px-6">
        <div className="bg-white rounded-2xl border border-card-border p-8 w-full max-w-sm shadow-sm text-center">
          <h1 className="font-display text-2xl font-bold text-text-dark mb-2">Admin Access</h1>
          <p className="text-text-gray text-sm mb-6">Sign in with your authorized Google account.</p>
          {signInError && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4 text-sm text-red-700">
              {signInError}
            </div>
          )}
          <button
            onClick={handleSignIn}
            className="w-full py-3 px-4 rounded-lg bg-brand-dark text-white font-semibold transition-transform duration-150 hover:scale-[1.01] flex items-center justify-center gap-3"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path fill="#fff" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/>
              <path fill="#fff" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#fff" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#fff" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Sign in with Google
          </button>
        </div>
      </div>
    )
  }

  // Dashboard
  return (
    <div className="min-h-screen bg-bg-main">
      {/* Hidden file inputs */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".pdf"
        className="hidden"
        onChange={handleFileSelected}
      />
      <input
        ref={newFileInputRef}
        type="file"
        accept=".pdf"
        className="hidden"
        onChange={handleNewFileUpload}
      />

      {/* Header */}
      <div className="bg-white border-b border-card-border px-6 py-4">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/" className="text-text-gray hover:text-text-dark transition-colors">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <h1 className="font-display text-xl font-bold text-text-dark">Resources</h1>
            <Link
              to="/admin/leads"
              className="text-sm text-brand-mid hover:text-brand-dark transition-colors"
            >
              Leads
            </Link>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-text-gray hidden sm:inline">{user.email}</span>
            <button
              onClick={() => newFileInputRef.current?.click()}
              disabled={uploading}
              className="inline-flex items-center gap-2 py-2 px-4 rounded-lg bg-brand-dark text-white text-sm font-semibold transition-transform duration-150 hover:scale-[1.02] disabled:opacity-50"
            >
              <Upload className="w-4 h-4" />
              Upload New
            </button>
            <button
              onClick={signOut}
              className="inline-flex items-center gap-2 py-2 px-3 rounded-lg border border-card-border text-text-gray text-sm hover:text-text-dark transition-colors"
              title="Sign out"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-8">
        {/* Search */}
        <div className="relative mb-6">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-gray" />
          <input
            type="text"
            placeholder="Search by title, slug, or comments..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-card-border bg-white text-sm text-text-dark placeholder:text-text-muted focus:outline-none focus:border-brand-mid transition-colors"
          />
        </div>

        {/* Content */}
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-8 h-8 animate-spin text-brand-mid" />
          </div>
        ) : error ? (
          <div className="bg-red-50 border border-red-200 rounded-xl p-6 flex items-start gap-4">
            <AlertCircle className="w-6 h-6 text-red-500 shrink-0" />
            <div>
              <h3 className="font-semibold text-red-800">Error</h3>
              <p className="text-red-700 mt-1">{error}</p>
            </div>
          </div>
        ) : filtered.length === 0 ? (
          <div className="bg-white rounded-xl border border-card-border p-12 text-center">
            <div className="text-5xl mb-4">📁</div>
            <h3 className="text-lg font-semibold text-text-dark mb-2">
              {searchQuery ? 'No matching resources' : 'No resources yet'}
            </h3>
            <p className="text-text-gray">
              {searchQuery
                ? 'Try a different search term.'
                : 'Upload PDF resources using the button above.'}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {filtered.map((resource) => (
              <div
                key={resource.slug}
                className="bg-white rounded-xl border border-card-border overflow-hidden"
              >
                {/* Resource row */}
                <div className="px-6 py-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-3 min-w-0">
                      <FileText className="w-5 h-5 text-brand-mid shrink-0 mt-0.5" />
                      <div className="min-w-0">
                        <h3 className="text-sm font-semibold text-text-dark truncate">
                          {resource.title}
                        </h3>
                        <p className="text-xs text-text-gray mt-0.5">
                          {resource.fileName} &middot; {formatFileSize(resource.size)} &middot;{' '}
                          {new Date(resource.uploadedAt).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {/* Download count badge */}
                      <button
                        onClick={() => handleToggleDownloaders(resource.slug)}
                        className={clsx(
                          'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors',
                          resource.downloadCount > 0
                            ? 'bg-blue-50 text-blue-700 hover:bg-blue-100'
                            : 'bg-gray-50 text-text-gray',
                        )}
                      >
                        <Users className="w-3.5 h-3.5" />
                        {resource.downloadCount}
                        {expandedSlug === resource.slug ? (
                          <ChevronUp className="w-3 h-3" />
                        ) : (
                          <ChevronDown className="w-3 h-3" />
                        )}
                      </button>

                      {/* Replace button */}
                      <button
                        onClick={() => handleReplaceClick(resource.slug)}
                        disabled={uploading && uploadSlug === resource.slug}
                        className="inline-flex items-center gap-1.5 py-1.5 px-3 rounded-lg border border-card-border text-text-gray text-xs hover:text-text-dark hover:border-brand-mid transition-colors disabled:opacity-50"
                      >
                        {uploading && uploadSlug === resource.slug ? (
                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        ) : (
                          <Replace className="w-3.5 h-3.5" />
                        )}
                        Replace
                      </button>
                    </div>
                  </div>

                  {/* Comments */}
                  <div className="mt-3 ml-8">
                    {editingSlug === resource.slug ? (
                      <div className="flex gap-2">
                        <textarea
                          value={editingComments}
                          onChange={(e) => setEditingComments(e.target.value)}
                          placeholder="Add notes about this resource..."
                          rows={2}
                          className="flex-1 text-sm border border-card-border rounded-lg px-3 py-2 text-text-dark placeholder:text-text-muted focus:outline-none focus:border-brand-mid resize-none"
                        />
                        <div className="flex flex-col gap-1">
                          <button
                            onClick={() => handleSaveComments(resource.slug)}
                            disabled={savingComments}
                            className="inline-flex items-center gap-1 py-1.5 px-3 rounded-lg bg-brand-dark text-white text-xs font-semibold transition-transform hover:scale-[1.02] disabled:opacity-50"
                          >
                            {savingComments ? (
                              <Loader2 className="w-3 h-3 animate-spin" />
                            ) : (
                              <Save className="w-3 h-3" />
                            )}
                            Save
                          </button>
                          <button
                            onClick={() => setEditingSlug(null)}
                            className="py-1.5 px-3 rounded-lg text-xs text-text-gray hover:text-text-dark transition-colors"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <button
                        onClick={() => {
                          setEditingSlug(resource.slug)
                          setEditingComments(resource.comments)
                        }}
                        className="text-sm text-text-gray hover:text-text-dark transition-colors text-left"
                      >
                        {resource.comments || (
                          <span className="italic text-text-muted">Click to add notes...</span>
                        )}
                      </button>
                    )}
                  </div>
                </div>

                {/* Expanded downloaders */}
                {expandedSlug === resource.slug && (
                  <div className="border-t border-card-border bg-gray-50 px-6 py-3">
                    {loadingDownloaders ? (
                      <div className="flex items-center gap-2 py-2">
                        <Loader2 className="w-4 h-4 animate-spin text-brand-mid" />
                        <span className="text-sm text-text-gray">Loading downloaders...</span>
                      </div>
                    ) : (downloaders[resource.slug]?.length || 0) === 0 ? (
                      <p className="text-sm text-text-gray py-1">No downloads yet.</p>
                    ) : (
                      <div className="max-h-48 overflow-y-auto">
                        <table className="min-w-full">
                          <thead>
                            <tr>
                              <th className="text-left text-xs font-medium text-text-gray uppercase pb-2">
                                Email
                              </th>
                              <th className="text-left text-xs font-medium text-text-gray uppercase pb-2">
                                Date
                              </th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200">
                            {downloaders[resource.slug]?.map((d, i) => (
                              <tr key={i}>
                                <td className="py-1.5 text-sm text-text-dark">{d.email}</td>
                                <td className="py-1.5 text-sm text-text-gray">
                                  {new Date(d.downloadedAt).toLocaleDateString()}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
